import { useState, useEffect } from "react";
import { Clock, Calendar } from "lucide-react";
import { useTranslation } from 'react-i18next';

export default function Countdown() {
  const { t } = useTranslation();
  const deadline = new Date("2025-10-25T23:59:59");
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = deadline - now;
      
      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true });
        clearInterval(timer);
        return;
      }
      
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / (1000 * 60)) % 60);
      const seconds = Math.floor((difference / 1000) % 60);
      
      setTimeLeft({ days, hours, minutes, seconds, isExpired: false });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (timeLeft.isExpired) {
    return (
      <div className="flex flex-col items-center space-y-4 mb-8">
        <div className="bg-red-600/90 backdrop-blur-sm text-white px-6 py-3 rounded-full shadow-2xl border border-red-400">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span className="text-base font-bold">{t('Countdown.title')}</span>
          </div>
        </div>
        <p className="text-gray-200 text-xs sm:text-sm">{t('Countdown.subtitle')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-3 sm:space-y-4 mb-6 w-full">
      <div className="flex items-center justify-center flex-wrap gap-2 sm:gap-4 bg-white/80 backdrop-blur-sm rounded-2xl px-4 sm:px-6 py-4 sm:py-6 shadow-2xl border border-yellow-400/30">
        {/* Days */}
        <div className="text-center">
          <div className="bg-gradient-to-b from-yellow-400 to-yellow-600 text-white rounded-xl px-3 py-2 sm:px-4 sm:py-3 w-14 sm:w-20 shadow-lg">
            <div className="text-xl sm:text-3xl font-bold mb-1">{timeLeft.days.toString().padStart(2, '0')}</div>
            <div className="text-[10px] sm:text-xs font-medium uppercase tracking-wider">{t('Countdown.days')}</div>
          </div>
        </div>

        <div className="text-yellow-400 text-xl sm:text-3xl font-bold animate-pulse">:</div>

        {/* Hours */}
        <div className="text-center">
          <div className="bg-gradient-to-b from-yellow-400 to-yellow-600 text-white rounded-xl px-3 py-2 sm:px-4 sm:py-3 w-14 sm:w-20 shadow-lg">
            <div className="text-xl sm:text-3xl font-bold mb-1">{timeLeft.hours.toString().padStart(2, '0')}</div>
            <div className="text-[10px] sm:text-xs font-medium uppercase tracking-wider">{t('Countdown.hours')}</div>
          </div>
        </div>

        <div className="text-yellow-400 text-xl sm:text-3xl font-bold animate-pulse">:</div>

        {/* Minutes */}
        <div className="text-center">
          <div className="bg-gradient-to-b from-yellow-400 to-yellow-600 text-white rounded-xl px-3 py-2 sm:px-4 sm:py-3 w-14 sm:w-20 shadow-lg">
            <div className="text-xl sm:text-3xl font-bold mb-1">{timeLeft.minutes.toString().padStart(2, '0')}</div>
            <div className="text-[10px] sm:text-xs font-medium uppercase tracking-wider">{t('Countdown.min')}</div>
          </div>
        </div>

        <div className="text-yellow-400 text-xl sm:text-3xl font-bold animate-pulse">:</div>

        {/* Seconds */}
        <div className="text-center">
          <div className="bg-gradient-to-b from-red-400 to-red-600 text-white rounded-xl px-3 py-2 sm:px-4 sm:py-3 w-14 sm:w-20 shadow-lg transform hover:scale-105 transition-transform">
            <div className="text-xl sm:text-3xl font-bold mb-1">{timeLeft.seconds.toString().padStart(2, '0')}</div>
            <div className="text-[10px] sm:text-xs font-medium uppercase tracking-wider">{t('Countdown.sec')}</div>
          </div>
        </div>
      </div>

      <div className="text-center">
        <p className="text-gray-200 text-xs sm:text-sm opacity-80">
          {t('Countdown.dont')}
        </p>
      </div>
    </div>
  );
}
